/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';

import { PCState } from '../../../core/enums';
import { ParentalControlServiceImpl } from '../../../core/impls/ParentalControlServiceImpl';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { updatePcState } from '../../../store/overlay/pcpinoverlay/pcpinoverlay.reducers';
import { setHideAdultContentStateAction } from '../../../store/settings/hideadultcontent/hideadultcontent.actions';
import {
    setPCMenuOptionsAction,
    setPCSettingVerticalLimitAction,
} from '../../../store/settings/parentalcontrols/parentalcontrols.actions';
import { onCursorStateChangeParentalSettingsOperations } from '../../../store/settings/parentalcontrols/parentalcontrols.reducers';
import { ClockTypes } from '../../Clock';
import { AntiburnOsd, ScreenHeader, ScreenPreview } from '../../common';
import { Whisper } from '../../Whisper';
import { WhisperTypes } from '../../Whisper/WhisperTypes';
import { Menu } from '../Menu';
import { Item } from '../Menu/MenuProps';
import { SettingsMenuTypes } from '../SettingsMenuTypes';
import { RATING_NAME_MAX_LENGTH } from './parentalcontrols.constants';

import './ParentalControls.scss';

const ParentalControls: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const services = new ServicesClientImpl();
    const { verticalPosition, pcState, isFocused, menuOptions } = useTivoSelector(
        (state: RootState) => state.parentalControls,
    );
    const { isActive: isAntiburnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const onOffStateTexts = [
        `${Locale.getString(LocaleKeys.SETTINGS_PC_OFF_TEXT)}`,
        `${Locale.getString(LocaleKeys.SETTINGS_PC_ON_TEXT)}`,
    ];
    const parentalControlsStateTexts = [
        `${onOffStateTexts[1]} - ${Locale.getString(LocaleKeys.SETTINGS_PC_LOCKED_TEXT)}`,
        `${onOffStateTexts[1]} - ${Locale.getString(LocaleKeys.SETTINGS_PC_UNLOCKED_TEXT)}`,
        onOffStateTexts[0],
    ];
    const [optionList, setOptionList] = useState<Item[]>(getOptionList());
    const [previewText, setPreviewText] = useState<string>('');
    const [screenPreviewClassName, setScreenPreviewClassName] = useState('');

    const parentalControlsDefaultMenu: ReactNode = (
        <div className="parental-controls-menu">
            <Menu
                items={optionList}
                focusedIndex={verticalPosition}
                isFocused={isFocused}
                selectedIndex={verticalPosition}
                name={SettingsMenuTypes.MENU}
            />
        </div>
    );

    function getOptionList(): Item[] {
        const currentPCState = services.getPCState();
        const highestRatings = [...services.getPCHighestAllowedRatings()];
        const ratingOptionList = services
            .getPCRatingsOptions()
            .map((rating) => ({ [rating.ratingTypeId]: rating.options }));
        const ratingNameList = ratingOptionList.map((rating, index) => {
            const ratingKey = Object.keys(rating)[0];
            const highestRatingKey = Object.keys(highestRatings[index])[0];
            return ratingKey === highestRatingKey
                ? Object.values(rating)[0][Object.values(highestRatings[index])[0]]
                : null;
        });
        let ratingValue = `${ratingNameList[0] || ''}${ratingNameList[1] ? `,${ratingNameList[1]}` : ''}`;
        let isTruncated = false;
        if (ratingValue.length >= RATING_NAME_MAX_LENGTH) {
            // Adding 2 to account for square brackets.
            ratingValue = ratingValue.substring(0, RATING_NAME_MAX_LENGTH + 2);
            isTruncated = true;
        }

        const defaultItems: Item[] = [
            {
                title: Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT),
                value: `[${parentalControlsStateTexts[currentPCState]}]`,
            },
            {
                title: Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_COTENT_TEXT),
                value: `[${services.isPCHideAdultContentEnabled() ? onOffStateTexts[1] : onOffStateTexts[0]}]`,
                isDisabled: currentPCState === PCState.Off,
            },
        ];

        if (ratingNameList.length)
            defaultItems.push({
                title: Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_TEXT),
                value: `[${ratingValue}]`,
                isDisabled: currentPCState === PCState.Off,
                isTruncated,
            });

        // Adding change PIN and Disable menu option when PC is not Off.
        if (currentPCState !== PCState.Off) {
            defaultItems.push(
                { title: Locale.getString(LocaleKeys.SETTINGS_PC_CHANGE_PIN_TEXT) },
                { title: Locale.getString(LocaleKeys.SETTINGS_PC_DISABLE_PARENTAL_CONTROLS_TEXT) },
            );
        }

        return defaultItems;
    }

    function getPreviewText(): string {
        const currentPCState = services.getPCState();

        const getParentalControlsText = () => {
            switch (currentPCState) {
                case PCState.Unlocked:
                    return [
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_UNLOCKED_FIRST_PREVIEW_TEXT)}`,
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_CURRENT_SETTING_TEXT)}:\n${parentalControlsStateTexts[currentPCState]}`,
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_UNLOCKED_SECOND_PREVIEW_TEXT)}`,
                    ];
                case PCState.Locked:
                    return [
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_LOCKED_PREVIEW_TEXT)}`,
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_CURRENT_SETTING_TEXT)}:\n${parentalControlsStateTexts[currentPCState]}`,
                    ];
                case PCState.Off:
                    return [
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_OFF_PREVIEW_TEXT)}`,
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_CURRENT_SETTING_TEXT)}:\n${parentalControlsStateTexts[currentPCState]}`,
                    ];
                default:
                    return [];
            }
        };

        const getSetRatingLimits = (): string[] => {
            const ratingOptions = services.getPCRatingsOptions();
            const heighestAllowedRatings = services.getPCHighestAllowedRatings();
            const unratedRatingAllowed = services.getPCUnratedContentAllowed();
            const unratedRatingOptions = services.getPCUnratedOptions();

            const ratingStrings: string[] = [
                Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_PREVIEW_TEXT),
            ];

            const getDescription = (ratingTypeId: string, highestRatingIndex: number): string => {
                if (!ratingOptions) return '';

                const rating = ratingOptions.find((r) => r.ratingTypeId === ratingTypeId);

                const highestRating = rating?.options[highestRatingIndex];
                let ratingName = highestRating;

                switch (highestRating) {
                    case ParentalControlServiceImpl.BLOCK_ALL_TEXT:
                        ratingName = Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT);
                        break;
                    case ParentalControlServiceImpl.ALLOW_ALL_TEXT:
                        ratingName = Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT);
                        break;
                    case ParentalControlServiceImpl.ALLOW_ALL_EXCEPT_ADULT_TEXT:
                        ratingName = `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_EXCEPT_TEXT)} ${Locale.getString(LocaleKeys.SETTINGS_PC_ADULT_TEXT)}`;
                        break;
                    default:
                }

                let description = `${rating?.text}: ${ratingName}`;

                // Highest allowed rating other than Block all & Allow all/Allow all except Adult.
                if (highestRatingIndex > 1) {
                    const ratingDesc = services.getPCRatingDescription(highestRating || '');
                    description += ` - ${ratingDesc}`;
                }

                return description;
            };

            // Highest allowed tv show.
            ratingStrings.push(
                `${Locale.getString(LocaleKeys.SETTINGS_PC_CURRENT_SETTINGS_TEXT)}:`,
            );

            heighestAllowedRatings.forEach((rating) => {
                ratingStrings.push(
                    getDescription(Object.keys(rating)[0], Number(Object.values(rating)[0])),
                );
            });

            unratedRatingOptions.forEach((rating, index) => {
                ratingStrings.push(
                    `${rating.text}: ${rating.options[Number(Object.values(Object.values(unratedRatingAllowed)[index])[0])]}`,
                );
            });

            return ratingStrings;
        };

        let previewList: string[];

        switch (menuOptions[verticalPosition]?.title) {
            case Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT):
                previewList = getParentalControlsText();
                break;
            case Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_COTENT_TEXT):
                previewList = [
                    `${Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_CONTENT_MAIN_PREVIEW_TEXT)}`,
                    `${Locale.getString(LocaleKeys.SETTINGS_PC_CURRENT_SETTING_TEXT)}:\n${onOffStateTexts[Number(services.isPCHideAdultContentEnabled())]}`,
                ];
                break;
            case Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_TEXT):
                previewList = getSetRatingLimits();
                break;
            case Locale.getString(LocaleKeys.SETTINGS_PC_CHANGE_PIN_TEXT):
                previewList = [
                    `${Locale.getString(LocaleKeys.SETTINGS_PC_CHANGE_PIN_PREVIEW_TEXT)}`,
                ];
                break;
            case Locale.getString(LocaleKeys.SETTINGS_PC_DISABLE_PARENTAL_CONTROLS_TEXT):
                previewList = [
                    `${Locale.getString(LocaleKeys.SETTINGS_PC_DISABLE_PARENTAL_CONTROLS_PREVIEW_TEXT)}`,
                ];
                break;
            default:
                previewList = [''];
        }

        return previewList.join('\n\n');
    }

    const onCursorStateChangeCallback = () => {
        dispatch(onCursorStateChangeParentalSettingsOperations());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    useEffect(() => {
        services.createOptionsList();
        const mOptionList = getOptionList();
        dispatch(setPCMenuOptionsAction(mOptionList));
        setOptionList(mOptionList);
        dispatch(setHideAdultContentStateAction(services.isPCHideAdultContentEnabled()));
        dispatch(setPCSettingVerticalLimitAction(mOptionList.length));
    }, [pcState]);

    useEffect(() => {
        if (pcState === PCState.Unlocked && isAntiburnOsdActive) {
            dispatch(updatePcState(PCState.Locked));
        }
    }, [isAntiburnOsdActive]);

    useEffect(() => {
        setPreviewText(getPreviewText());
        let previewClassName = 'preview';
        if (
            menuOptions[verticalPosition]?.title ===
            Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_TEXT)
        ) {
            previewClassName += ' preview-text-reduced-font-size';
        }

        setScreenPreviewClassName(previewClassName);
    }, [verticalPosition, optionList]);

    return (
        <div className="parental-controls-container">
            <ScreenHeader
                title={Locale.getString(LocaleKeys.SETTINGS_PC_HEADER_TITLE_TEXT)}
                clockType={ClockTypes.DEFAULT_CLOCK}
            />
            <div className="parental-controls-body">{parentalControlsDefaultMenu}</div>
            <ScreenPreview classStyle={`${screenPreviewClassName}`}>{previewText}</ScreenPreview>
            <Whisper
                message={Locale.getString(LocaleKeys.SETTINGS_PC_CHANGE_PIN_WHISPER_MESSAGE)}
                iconType={WhisperTypes.INFO}
            />
            {isAntiburnOsdActive && <AntiburnOsd parentalCheck={false} />}
        </div>
    );
};

export default ParentalControls;
